import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, BookOpen, Gauge } from 'lucide-react';
import TTSButton from '../components/TTSButton';
import MarkdownExplanation from '../components/MarkdownExplanation';
import {
  fetchConceptPlus1WithDifficulty,
  recordConceptCompletion,
  getCompletionCounts,
  getPriorCompletionDifficulties,
} from '../services/conceptPlus1Service';
import type { ConceptPlus1 } from '../types';
import { PLUS1_SUBJECTS } from '../types';
import { getStoredSession } from '../hooks/useAuth';
import { useBadgeCheck } from '../hooks/useBadgeCheck';
import type { AssignmentContext } from '../services/assignmentService';
import { isFeatureEnabled } from '../config/regionConfig';
import ExplorePanel from '../components/explore/ExplorePanel';
import ConceptVideoPlayer from '../components/ConceptVideoPlayer';
import AppliedPracticeSection from '../components/AppliedPracticeSection';
import { MultiBadgeNotification } from '../components/MultiBadgeNotification';
import { isChatbotEnabledForPlan } from '../config/features';
import { FloatingTabBar } from '../components/FloatingTabBar';

const DoubtChatbotTemplate = lazy(() =>
  import('../components/DoubtChatbotTemplate').then((m) => ({ default: m.DoubtChatbotTemplate }))
);

type Difficulty = 'basic' | 'standard' | 'advanced';

const difficultyStyles = {
  basic: {
    bg: 'bg-green-50',
    border: 'border-green-500',
    text: 'text-green-700',
    label: 'Basic',
    gradient: 'from-green-500 to-emerald-600'
  },
  standard: {
    bg: 'bg-orange-50',
    border: 'border-orange-500',
    text: 'text-orange-700',
    label: 'Standard',
    gradient: 'from-orange-500 to-amber-600'
  },
  advanced: {
    bg: 'bg-purple-50',
    border: 'border-purple-500',
    text: 'text-purple-700',
    label: 'Advanced',
    gradient: 'from-purple-500 to-pink-600'
  }
};

const DIFFICULTY_RANK: Record<string, number> = { basic: 1, standard: 2, advanced: 3 };

export default function ConceptPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const assignmentContext = (location.state as { assignment?: AssignmentContext } | null)?.assignment ?? null;
  const [concept, setConcept] = useState<ConceptPlus1 | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [difficulty, setDifficulty] = useState<Difficulty>('standard');
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const appliedPracticeRef = useRef<HTMLDivElement>(null);
  const session = getStoredSession();

  const studentId = session?.student_id;
  const { awardByCriteria, pendingBadges, dismissFirst } = useBadgeCheck(studentId);
  const completionFiredRef = useRef<Partial<Record<string, boolean>>>({});

  async function handlePracticeAllAnswered(diff: string) {
    const conceptId = Number(id);
    if (!studentId || !conceptId) return;
    const key = `${conceptId}-${diff}`;
    if (completionFiredRef.current[key]) return;
    completionFiredRef.current[key] = true;

    recordConceptCompletion(studentId, conceptId, diff.toLowerCase());

    const [counts, priorDifficulties] = await Promise.all([
      getCompletionCounts(studentId),
      getPriorCompletionDifficulties(studentId, conceptId),
    ]);

    const total = counts.total + 1;
    const today = counts.today + 1;

    if (total === 1)  awardByCriteria('concepts_completed:1');
    if (total === 10) awardByCriteria('concepts_10');
    if (total === 25) awardByCriteria('concepts_completed:25');
    if (total === 60) awardByCriteria('concepts_completed:60');
    if (today >= 10)  awardByCriteria('concepts_in_day:10');

    const currentRank = DIFFICULTY_RANK[diff.toLowerCase()] ?? 0;
    const hadLower = priorDifficulties.some(d => (DIFFICULTY_RANK[d] ?? 0) < currentRank);
    if (hadLower) awardByCriteria('going_deeper');
  }

  useEffect(() => {
    async function loadConcept() {
      if (!id) return;
      setIsLoading(true);
      const conceptData = await fetchConceptPlus1WithDifficulty(Number(id), difficulty);
      setConcept(conceptData);
      setIsLoading(false);
    }
    loadConcept();
  }, [id, difficulty]);

  useEffect(() => {
    if (concept && concept.subject_name === 'English') {
      navigate('/concepts', { replace: true });
    }
  }, [concept, navigate]);

  // Deep-link support: a concept page URL with ?chinni=1 opens the chatbot
  // automatically instead of requiring the manual toggle click — same
  // enablement gate as the toggle itself. Dashboard's "Ask Chinni" card no
  // longer routes through here (it opens DoubtChatbotTemplate directly,
  // since it doesn't actually need a concept context), but this stays as a
  // standalone deep-link entry point.
  useEffect(() => {
    if (!concept) return;
    const params = new URLSearchParams(location.search);
    if (params.get('chinni') === '1' && isChatbotEnabledForPlan(session?.planType)) {
      setIsChatbotOpen(true);
    }
  }, [concept, location.search, session?.planType]);

  // Phase 8 Exam Prep orchestration's "Continue Practice Sheet" row routes
  // here with ?practice=1 (same query-param convention as ?chinni=1 above) so
  // the student lands straight on Applied Practice instead of the top of the
  // concept page. AppliedPracticeSection itself is unchanged -- it already
  // loads its own questions on mount.
  useEffect(() => {
    if (!concept) return;
    const params = new URLSearchParams(location.search);
    if (params.get('practice') === '1') {
      appliedPracticeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [concept, location.search]);

  if (isLoading) {
    return (
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-100 rounded w-1/4 mb-8"></div>
          <div className="glass-card rounded-xl p-5 sm:p-6">
            <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-100 rounded"></div>
              <div className="h-4 bg-gray-100 rounded"></div>
              <div className="h-4 bg-gray-100 rounded w-3/4"></div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!concept) {
    return (
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8">
        <div className="text-center py-12 glass-card rounded-xl">
          <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Concept not found</p>
          <button
            onClick={() => navigate('/concepts')}
            className="text-coral-warm hover:underline mt-2 inline-block font-medium"
          >
            Back to concepts
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="ocean-bg flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 pt-24 pb-24">
      {/* Header */}
      <div className="glass-ocean rounded-xl sticky top-20 z-20 p-4 mb-6 shadow-lg animate-fade-in-up">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
          <button
            onClick={() => navigate('/concepts')}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors self-start"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back to Concepts</span>
          </button>

          {/* Difficulty Selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-white/60">Level:</span>
            <div className="relative">
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value as Difficulty)}
                className={`appearance-none ${difficultyStyles[difficulty].bg} border-2 ${difficultyStyles[difficulty].border} rounded-lg px-3 py-1.5 pr-7 text-sm font-medium ${difficultyStyles[difficulty].text} cursor-pointer focus:outline-none focus:ring-2 focus:ring-bio-cyan`}
              >
                <option value="basic">Basic</option>
                <option value="standard">Standard</option>
                <option value="advanced">Advanced</option>
              </select>
              <Gauge className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        <h1 className="text-lg sm:text-xl font-bold text-white">{concept.name}</h1>
        <p className="text-white/60 text-xs sm:text-sm mt-1">
          {concept.subject_name || PLUS1_SUBJECTS[concept.subject_id || 0]}
          {concept.grade_range && ` | Class ${concept.grade_range.join(', ')}`}
        </p>
      </div>

      {/* Assignment banner */}
      {assignmentContext && (
        <div className="flex items-center gap-3 px-4 py-3 mb-6 rounded-xl bg-coral-warm/20 border border-coral-warm/40 animate-fade-in-up">
          <span className="text-orange-300 text-lg flex-shrink-0">📋</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white leading-tight">
              Your teacher assigned this
              {' — '}
              <span className="capitalize">{assignmentContext.type}</span> mode
            </p>
            <p className="text-xs text-white/60 mt-0.5">
              Due {new Date(assignmentContext.due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
            </p>
          </div>
        </div>
      )}

      {/* Hook Card — short explanation + TTS */}
      <div className="glass-ocean rounded-xl p-4 sm:p-6 mb-6 border-l-4 border-bio-mint animate-fade-in-up stagger-1">
        <div className="flex items-start gap-2 sm:gap-3">
          <div className="p-2 bg-gradient-to-br from-bio-mint/30 to-bio-cyan/20 rounded-lg flex-shrink-0">
            <BookOpen className="w-5 h-5 text-bio-mint" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-xs sm:text-sm font-bold text-bio-mint uppercase tracking-wide">Key Idea</h2>
              <TTSButton text={concept.explanation_standard || ''} />
            </div>
            <MarkdownExplanation
              text={concept.explanation_standard || 'No explanation available.'}
              variant="dark"
              className="text-sm sm:text-base text-white/90 leading-relaxed"
            />
          </div>
        </div>
      </div>

      {/* Explainer Video */}
      <ConceptVideoPlayer conceptId={Number(id)} sourceTable="concepts_plus1" />

      {/* Explore Panel */}
      {isFeatureEnabled('showExplorePanel') && (
        <div className="mb-6">
          <ExplorePanel
            conceptId={Number(id)}
            conceptName={concept.name}
            gradeLevel={session?.grade_level ?? 11}
            board={session?.board ?? 'CBSE'}
            studentId={session?.student_id ?? ''}
            subjectName={concept.subject_name ?? String(concept.subject_id ?? '')}
            realLifeExample={concept.real_life_example}
            explanationBasic={concept.explanation_basic}
            explanationStandard={concept.explanation_standard}
            explanationAdvanced={concept.explanation_advanced}
            assignmentContext={assignmentContext ?? undefined}
            onAwardBadge={awardByCriteria}
          />
        </div>
      )}

      {/* Applied Practice */}
      <div ref={appliedPracticeRef}>
        <AppliedPracticeSection
          conceptId={Number(id)}
          conceptName={concept.name}
          subjectName={concept.subject_name ?? String(concept.subject_id ?? '')}
          gradeLevel={session?.grade_level ?? 11}
          studentId={session?.student_id ?? ''}
          conceptType={concept.concept_type ?? undefined}
          assignmentContext={assignmentContext ?? undefined}
          onAllAnswered={handlePracticeAllAnswered}
          onAwardBadge={awardByCriteria}
        />
      </div>

      {pendingBadges.length > 0 && (
        <MultiBadgeNotification badges={pendingBadges} onClose={dismissFirst} />
      )}

      {isChatbotEnabledForPlan(session?.planType) && concept && (
        <>
          <FloatingTabBar
            activeTab="flashcards"
            onTabChange={() => {}}
            flashcardCount={0}
            showFlashcards={false}
            showTests={false}
            showChatbot
            isChatbotOpen={isChatbotOpen}
            onChatbotToggle={() => setIsChatbotOpen(!isChatbotOpen)}
          />
          <Suspense fallback={null}>
            <DoubtChatbotTemplate
              conceptId={Number(id)}
              conceptName={concept.name}
              externalOpen={isChatbotOpen}
              onExternalOpenChange={setIsChatbotOpen}
            />
          </Suspense>
        </>
      )}

    </main>
  );
}
